import { EyeOff, Link2Off, Inbox } from 'lucide-react';

import { DepositsContainer } from 'src/containers/SoftwareEscrow/Deposits';
import { EmptyBlock } from '@/components/ui/empty-block';
import { EscrowStatusEnum, EscrowPartyTypeEnum, InvitationStatusEnum } from 'src/graphql/schema';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { cn } from 'src/lib/utils';

const EmptyBlockWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="container mx-auto h-full flex flex-col space-y-6">
      <div className="space-y-1">
        <div className="text-base font-semibold text-gray-700">Deposits</div>
        <p className="text-sm text-gray-500">
          All assets associated to your escrow. Assets connected through integrations are checked for updates daily.
        </p>
      </div>
      {children}
    </div>
  );
};

const SoftwareEscrowDeposits = () => {
  const {
    softwareEscrow: { status, beneficiarySharedInformation, currentCompanyRole, escrowInvitation, activatedAt },
  } = useSoftwareEscrow();

  if (status === EscrowStatusEnum.Terminated)
    return (
      <EmptyBlockWrapper>
        <EmptyBlock
          title="This escrow has been terminated."
          description="The material associated with this application is no longer in escrow."
          icon={Link2Off}
          classes={{
            title: 'text-base',
            card: 'grow-1 [&>div]:h-full',
            content: 'h-full flex flex-col justify-center',
          }}
        />
      </EmptyBlockWrapper>
    );

  if (!beneficiarySharedInformation && currentCompanyRole === EscrowPartyTypeEnum.Beneficiary)
    return (
      <EmptyBlockWrapper>
        <EmptyBlock
          title="Hidden assets"
          description={`Asset details are available to users with asset visibility. To request access, go to the settings tab in this escrow and Request changes\nunder Technical Information > Notifications settings for beneficiaries and select “Show assets.”`}
          icon={EyeOff}
          classes={{
            title: 'text-base',
            card: 'grow-1 [&>div]:h-full',
            content: 'h-full flex flex-col justify-center',
          }}
        />
      </EmptyBlockWrapper>
    );

  if (status === EscrowStatusEnum.Signed || Boolean(activatedAt)) {
    return (
      <div className="container mx-auto space-y-4">
        <DepositsContainer />
      </div>
    );
  }

  return (
    <EmptyBlockWrapper>
      <EmptyBlock
        title="No assets deposited yet"
        description="The depositor will begin adding assets here once the escrow is signed."
        icon={Inbox}
        classes={{
          title: 'text-base',
          card: cn('grow-1 [&>div]:h-full', {
            'opacity-70 pointer-events-none': escrowInvitation?.status === InvitationStatusEnum.Rejected,
          }),
          content: 'h-full flex flex-col justify-center',
        }}
      />
    </EmptyBlockWrapper>
  );
};

export default SoftwareEscrowDeposits;
